const DEFAULT_SITE_CATEGORY = {
  slug: 'desk-setup',
  shortLabel: 'Desk setup',
  title: 'Desk setup ideas',
  description: 'Browse real desk setups, discover tagged gear, and explore related workspace ideas on 101Setups.',
};

export const SETUP_CATEGORY_DEFINITIONS = [
  {
    slug: 'cable-management',
    shortLabel: 'Cable management',
    title: 'Desk cable management ideas',
    description:
      'See real desk setups with cable trays, under-desk routing, and cleaner wiring inspiration for home offices and gaming desks.',
    intro:
      'Cable clutter is one of the fastest ways for a workspace to feel unfinished. This hub pulls together real setups that surface trays, sleeves, and cleaner under-desk routing ideas so readers can copy practical cable-management moves instead of staring at generic product grids.',
    keywords: ['cable', 'wire', 'tray', 'sleeve', 'raceway', 'organizer', 'under desk'],
    tagCategories: ['Cablemanagement'],
    relatedSlugs: ['monitor-arms', 'desk-lighting', 'standing-desk'],
  },
  {
    slug: 'monitor-arms',
    shortLabel: 'Monitor arms',
    title: 'Monitor arm and stand setups',
    description:
      'Browse real desk setups featuring monitor arms, ultrawide layouts, monitor stands, and cleaner screen positioning ideas.',
    intro:
      'Monitor placement shapes the whole desk. These setups highlight monitor arms, stands, and layout decisions that make a workspace feel cleaner, more ergonomic, and easier to copy whether you are building around one display, dual screens, or an ultrawide.',
    keywords: ['monitor arm', 'monitor stand', 'ultrawide', 'mount', 'vesa', 'screen', 'display', 'monitor'],
    tagCategories: ['Monitorstand', 'Monitor'],
    relatedSlugs: ['desk-shelves', 'small-desk', 'cable-management'],
  },
  {
    slug: 'desk-shelves',
    shortLabel: 'Desk shelves',
    title: 'Desk shelf and riser ideas',
    description:
      'Explore real desk setups using desk shelves, risers, and layered monitor storage to create more space and cleaner layouts.',
    intro:
      'Desk shelves and risers help a setup earn back space without forcing a full furniture upgrade. This starter hub collects setups where raised screens, shelves, and layered surfaces create room for keyboards, notebooks, and accessories while keeping the desk intentional.',
    keywords: ['shelf', 'riser', 'monitor stand', 'risers', 'shelving', 'shelfie'],
    tagCategories: ['Monitorstand', 'Desk'],
    relatedSlugs: ['monitor-arms', 'small-desk', 'desk-lighting'],
  },
  {
    slug: 'desk-lighting',
    shortLabel: 'Desk lighting',
    title: 'Desk lighting setup ideas',
    description:
      'Find real setups with monitor light bars, desk lamps, and lighting details that improve comfort and visual polish.',
    intro:
      'Lighting changes how a workspace looks and how it feels to use late into the day. These setups gather monitor light bars, lamps, and glow-focused details that make desk lighting easier to browse in context instead of as isolated product listings.',
    keywords: ['light', 'lamp', 'light bar', 'lighting', 'glow', 'backlight', 'monitor light'],
    tagCategories: ['Light', 'Lamp'],
    relatedSlugs: ['cable-management', 'desk-shelves', 'small-desk'],
  },
  {
    slug: 'small-desk',
    shortLabel: 'Small desks',
    title: 'Small desk setup ideas',
    description:
      'Browse compact desk setups, laptop-and-monitor layouts, and small-space workspace ideas built from real posts.',
    intro:
      'Small desks force better choices. This hub brings together compact layouts, laptop-and-monitor combinations, and tighter workspace ideas so readers can see how real setups handle limited surface area without giving up useful gear.',
    keywords: ['small desk', 'compact', 'laptop', 'notebook', 'portable', 'small space'],
    tagCategories: ['Notebook', 'Desk'],
    relatedSlugs: ['desk-shelves', 'monitor-arms', 'desk-lighting'],
  },
  {
    slug: 'standing-desk',
    shortLabel: 'Standing desks',
    title: 'Standing desk setup ideas',
    description:
      'See real standing desk setups with cable management, screen positioning, and accessories that fit sit-stand workstations.',
    intro:
      'Standing desks need their own layout logic: cleaner cable runs, adaptable screen placement, and accessories that still work when the desk moves. This hub starts collecting setups that can support future standing-desk accessory and cable-management pages.',
    keywords: ['standing desk', 'sit stand', 'standing', 'adjustable desk', 'walking pad', 'treadmill'],
    tagCategories: ['Desk'],
    relatedSlugs: ['cable-management', 'monitor-arms', 'small-desk'],
  },
];

const slugify = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const truncateWords = (value, maxWords = 14) => {
  const words = String(value || '')
    .split(/\s+/)
    .filter(Boolean);

  return words.slice(0, maxWords).join(' ');
};

export const parseCreatorName = rawUserId => {
  if (!rawUserId) {
    return 'community';
  }

  try {
    const parsed = JSON.parse(rawUserId);
    if (typeof parsed === 'string' && parsed.trim()) {
      return parsed.trim();
    }
  } catch (error) {
    // ignore malformed JSON-like values and fall back below
  }

  return String(rawUserId).replace(/^"+|"+$/g, '').trim() || 'community';
};

export const parseListTags = rawListTags => {
  if (!rawListTags) {
    return [];
  }

  if (Array.isArray(rawListTags)) {
    return rawListTags;
  }

  try {
    const parsed = JSON.parse(rawListTags);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const getTagSummaries = post => {
  const parsedTags = parseListTags(post?.listtags);

  return parsedTags
    .map(entry => ({
      name: entry?.tag?.name || '',
      category: entry?.tag?.category || '',
    }))
    .filter(tag => tag.name || tag.category);
};

export const getUniqueTagNames = post => {
  const tagNames = getTagSummaries(post)
    .map(tag => tag.name.trim())
    .filter(Boolean);

  return [...new Set(tagNames)];
};

export const getUniqueTagCategories = post => {
  const categories = getTagSummaries(post)
    .map(tag => tag.category.trim())
    .filter(Boolean);

  return [...new Set(categories)];
};

export const getCategoryBySlug = slug =>
  SETUP_CATEGORY_DEFINITIONS.find(category => category.slug === slug) || null;

export const getRelatedCategories = category => {
  if (!category) {
    return SETUP_CATEGORY_DEFINITIONS.slice(0, 3);
  }

  return category.relatedSlugs
    .map(getCategoryBySlug)
    .filter(Boolean)
    .slice(0, 3);
};

export const scorePostForCategory = (post, category) => {
  if (!post || !category) {
    return 0;
  }

  const tags = getTagSummaries(post);
  const creator = parseCreatorName(post.userid).toLowerCase();
  let score = 0;

  tags.forEach(tag => {
    const lowerName = tag.name.toLowerCase();

    if (category.tagCategories.includes(tag.category)) {
      score += 4;
    }

    category.keywords.forEach(keyword => {
      if (lowerName.includes(keyword)) {
        score += keyword.includes(' ') ? 4 : 2;
      }
    });
  });

  category.keywords.forEach(keyword => {
    if (creator.includes(keyword)) {
      score += 1;
    }
  });

  return score;
};

export const getRankedPostsForCategory = (posts, category) =>
  [...posts]
    .map(post => ({
      post,
      score: scorePostForCategory(post, category),
    }))
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      const leftDate = new Date(left.post?.createdAt || left.post?.timestamp || 0).getTime();
      const rightDate = new Date(right.post?.createdAt || right.post?.timestamp || 0).getTime();

      return rightDate - leftDate;
    });

export const getPrimaryCategoryForPost = post => {
  const ranked = SETUP_CATEGORY_DEFINITIONS.map(category => ({
    category,
    score: scorePostForCategory(post, category),
  })).sort((left, right) => right.score - left.score);

  if (!ranked[0] || ranked[0].score <= 0) {
    return null;
  }

  return ranked[0].category;
};

export const getSetupSlug = post => {
  const creator = parseCreatorName(post?.userid);
  const primaryCategory = getPrimaryCategoryForPost(post);
  const tagNames = getUniqueTagNames(post).slice(0, 3);
  const baseSlug = slugify([
    primaryCategory?.slug || DEFAULT_SITE_CATEGORY.slug,
    creator,
    ...tagNames,
  ].join(' '));

  return `${baseSlug || DEFAULT_SITE_CATEGORY.slug}--${post?.id}`;
};

export const getSetupPath = post => `/setup/${getSetupSlug(post)}`;

export const getPostIdFromSetupSlug = slug => {
  if (!slug) {
    return '';
  }

  const parts = String(slug).split('--');
  return parts.length > 1 ? parts[parts.length - 1] : String(slug);
};

export const buildSetupTitle = post => {
  const primaryCategory = getPrimaryCategoryForPost(post);
  const tagNames = getUniqueTagNames(post);

  if (primaryCategory && tagNames.length > 0) {
    return `${primaryCategory.shortLabel} setup with ${truncateWords(tagNames.slice(0, 2).join(' and '), 10)}`;
  }

  if (tagNames.length > 0) {
    return `${truncateWords(tagNames.slice(0, 2).join(' and '), 10)} desk setup`;
  }

  return 'Real desk setup inspiration';
};

export const buildSetupDescription = post => {
  const primaryCategory = getPrimaryCategoryForPost(post) || DEFAULT_SITE_CATEGORY;
  const creator = parseCreatorName(post?.userid);
  const tagNames = getUniqueTagNames(post);
  const leadTags = tagNames.slice(0, 4).join(', ');

  if (leadTags) {
    return `Explore this real ${primaryCategory.shortLabel.toLowerCase()} setup by ${creator} and discover tagged gear including ${leadTags}.`;
  }

  return `Explore this real desk setup by ${creator}, browse tagged gear, and find related workspace inspiration on 101Setups.`;
};

export const buildSetupStory = post => {
  const primaryCategory = getPrimaryCategoryForPost(post) || DEFAULT_SITE_CATEGORY;
  const tagNames = getUniqueTagNames(post);

  if (tagNames.length > 0) {
    return `This setup fits the ${primaryCategory.shortLabel.toLowerCase()} theme and surfaces gear like ${tagNames.slice(0, 5).join(', ')}. Use it as a practical reference point, then open related setup ideas to compare layouts, accessories, and recurring gear patterns.`;
  }

  return `This real setup gives 101Setups another leaf page for browsing gear in context. Open related setup ideas and category hubs to keep exploring instead of dead-ending on a single image.`;
};

export const buildSetupAltText = post => {
  const title = buildSetupTitle(post);
  const tagNames = getUniqueTagNames(post);

  if (tagNames.length > 0) {
    return `${title} featuring ${tagNames.slice(0, 4).join(', ')}`;
  }

  return title;
};

export const getSetupPageData = post => {
  const primaryCategory = getPrimaryCategoryForPost(post) || null;
  const creator = parseCreatorName(post?.userid);
  const tags = getUniqueTagNames(post);
  const categories = getUniqueTagCategories(post);
  const title = buildSetupTitle(post);
  const description = buildSetupDescription(post);

  return {
    creator,
    tags,
    categories,
    primaryCategory,
    title,
    description,
    excerpt: description,
    story: buildSetupStory(post),
    altText: buildSetupAltText(post),
    path: getSetupPath(post),
  };
};

const shareWeight = (currentTags, candidateTags) => {
  const currentTagSet = new Set(currentTags.map(tag => tag.toLowerCase()));
  return candidateTags.reduce((score, tag) => score + (currentTagSet.has(tag.toLowerCase()) ? 2 : 0), 0);
};

export const getRelatedPosts = (posts, currentPost, limit = 3) => {
  const currentData = getSetupPageData(currentPost);
  const currentTags = currentData.tags;
  const currentCategory = currentData.primaryCategory;

  return [...posts]
    .filter(candidate => candidate?.id !== currentPost?.id)
    .map(candidate => {
      const candidateData = getSetupPageData(candidate);
      const tagScore = shareWeight(currentTags, candidateData.tags);
      const categoryScore =
        currentCategory && candidateData.primaryCategory?.slug === currentCategory.slug ? 4 : 0;

      return {
        candidate,
        score: tagScore + categoryScore,
      };
    })
    .sort((left, right) => right.score - left.score)
    .slice(0, limit)
    .map(entry => entry.candidate);
};
